import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../lib/AuthContext";
import { fetchInventoryOwners } from "../apis/api.inventory";
import { queryInventoryOwnersKey } from "../apis/queryKeys";

// Dueño del inventario que se está operando. Profesional / institución: el propio.
// Secretaria: uno de sus profesionales, elegido con InventoryOwnerSelector y persistido
// en localStorage por usuario; se sincroniza entre componentes con un evento propio.
// Ver CLAUDE.md → "Módulo de Inventario".

const KEY_PREFIX = "binapsis.inventory-owner";
const EVENT = "inventory-owner-change";

const storageKey = (userId) => `${KEY_PREFIX}:${userId || "anon"}`;

const readStored = (userId) => {
  try {
    return localStorage.getItem(storageKey(userId)) || null;
  } catch {
    return null;
  }
};

export default function useInventoryOwner() {
  const { user } = useAuth();
  const userId = user?.id;
  const isSecretary = user?.role === "secretary";
  const [storedId, setStoredId] = useState(() => readStored(userId));

  useEffect(() => {
    setStoredId(readStored(userId));
    const handler = () => setStoredId(readStored(userId));
    window.addEventListener(EVENT, handler);
    window.addEventListener("storage", handler); // otras pestañas
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, [userId]);

  const { data: owners = [], isLoading } = useQuery({
    queryKey: queryInventoryOwnersKey(userId),
    queryFn: () => fetchInventoryOwners(userId),
    enabled: !!userId && isSecretary,
  });

  const setOwnerId = useCallback(
    (id) => {
      try {
        localStorage.setItem(storageKey(userId), id);
      } catch {
        // sin storage: queda la selección por defecto
      }
      window.dispatchEvent(new Event(EVENT));
    },
    [userId]
  );

  const owner = useMemo(() => {
    if (!isSecretary) return user || null;
    return owners.find((o) => o.id === storedId) || owners[0] || null;
  }, [isSecretary, user, owners, storedId]);

  return {
    ownerId: owner?.id || null,
    owner,
    owners,
    setOwnerId,
    isSecretary,
    isLoading: isSecretary && isLoading,
  };
}
